import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  PaymentRequestButtonElement,
  useStripe
} from '@stripe/react-stripe-js';
import { PaymentRequest } from '@stripe/stripe-js';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import {
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { cn, toStripeMinorUnits } from '@vaiu/utils';
import { i18n } from '@/utils/i18n';

interface DigitalWalletButtonProps {
  amount: number;
  currency: string;
  label?: string;
  onPaymentSuccess: (paymentMethod: any) => void | Promise<void>;
  onPaymentError?: (error: Error) => void;
  className?: string;
  disabled?: boolean;
  showFallback?: boolean;
}

/**
 * DigitalWalletButton - Apple Pay / Google Pay via Stripe Payment Request Button.
 */
const DigitalWalletButton: React.FC<DigitalWalletButtonProps> = ({
  amount,
  currency,
  label = 'Total',
  onPaymentSuccess,
  onPaymentError,
  className,
  disabled = false,
  showFallback = true
}) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  const onPaymentErrorRef = useRef(onPaymentError);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    onPaymentSuccessRef.current = onPaymentSuccess;
    onPaymentErrorRef.current = onPaymentError;
  }, [onPaymentSuccess, onPaymentError]);

  const paymentRequestParams = useMemo(() => ({
    country: 'US',
    currency: currency.toLowerCase(),
    total: {
      label,
      amount: toStripeMinorUnits(amount, currency),
    },
    requestPayerName: true,
    requestPayerEmail: true,
  }), [currency, label, amount]);

  useEffect(() => {
    let cancelled = false;

    if (!stripe || amount <= 0) {
      setPaymentRequest(null);
      setCanMakePayment(false);
      setIsChecking(Boolean(!stripe && amount > 0));
      return;
    }

    setIsChecking(true);
    setCanMakePayment(false);
    setPaymentRequest(null);

    const pr = stripe.paymentRequest(paymentRequestParams);

    pr.on('paymentmethod', async (event) => {
      if (isProcessingRef.current) {
        console.warn('[DigitalWallet] Payment already processing, ignoring duplicate event');
        event.complete('fail');
        return;
      }

      isProcessingRef.current = true;
      setIsProcessing(true);
      setError(null);

      console.log('[DigitalWallet] Payment method received:', {
        id: event.paymentMethod.id,
        type: event.paymentMethod.type,
        wallet: event.paymentMethod.wallet?.type,
        amount,
        currency
      });

      const paymentState = {
        paymentMethodId: event.paymentMethod.id,
        amount,
        currency,
        timestamp: Date.now()
      };
      sessionStorage.setItem('pendingDigitalWalletPayment', JSON.stringify(paymentState));

      try {
        await onPaymentSuccessRef.current(event.paymentMethod);
        sessionStorage.removeItem('pendingDigitalWalletPayment');
        console.log('[DigitalWallet] Payment processing completed successfully');
        event.complete('success');
      } catch (err: any) {
        console.error('[DigitalWallet] Payment processing failed:', err);
        setError(err.message || i18n.__('vaiu.topup.wallet.payment_failed'));
        sessionStorage.removeItem('pendingDigitalWalletPayment');
        event.complete('fail');
        onPaymentErrorRef.current?.(err);
      } finally {
        isProcessingRef.current = false;
        setIsProcessing(false);
      }
    });

    pr.canMakePayment()
      .then((result) => {
        if (cancelled) {
          pr.removeAllListeners();
          return;
        }
        const available = Boolean(
          result && (result.applePay || result.googlePay || result.link)
        );
        console.log('[DigitalWallet] Payment Request canMakePayment result:', result);
        if (available) {
          if (result?.applePay) console.log('[DigitalWallet] Apple Pay is available');
          if (result?.googlePay) console.log('[DigitalWallet] Google Pay is available');
          if (result?.link) console.log('[DigitalWallet] Link is available');
          setPaymentRequest(pr);
          setCanMakePayment(true);
        } else {
          console.log('[DigitalWallet] No digital wallets available');
          pr.removeAllListeners();
          setPaymentRequest(null);
          setCanMakePayment(false);
        }
        setIsChecking(false);
      })
      .catch((err) => {
        if (cancelled) {
          pr.removeAllListeners();
          return;
        }
        console.error('[DigitalWallet] Error checking payment availability:', err);
        pr.removeAllListeners();
        setPaymentRequest(null);
        setCanMakePayment(false);
        setIsChecking(false);
      });

    return () => {
      cancelled = true;
      pr.removeAllListeners();
      isProcessingRef.current = false;
    };
  }, [stripe, amount, currency, paymentRequestParams]);

  if (isChecking) {
    return (
      <div className={cn('text-sm text-muted-foreground py-2', className)}>
        {i18n.__('vaiu.topup.wallet.checking')}
      </div>
    );
  }

  if (!canMakePayment || !paymentRequest) {
    if (!showFallback) {
      return null;
    }

    return (
      <div className={cn('space-y-2', className)}>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              <p>{i18n.__('vaiu.topup.wallet.unavailable')}</p>
              <ul className="text-xs ml-4 mt-1 space-y-0.5">
                <li>• {i18n.__('vaiu.topup.wallet.hint.browsers')}</li>
                <li>• {i18n.__('vaiu.topup.wallet.hint.https')}</li>
                <li>• {i18n.__('vaiu.topup.wallet.hint.iframe')}</li>
              </ul>
              <p className="mt-2">{i18n.__('vaiu.topup.wallet.use_card')}</p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="relative">
        <PaymentRequestButtonElement
          options={{
            paymentRequest,
            style: {
              paymentRequestButton: {
                type: 'default',
                theme: 'dark',
                height: '48px'
              }
            }
          }}
          className="w-full"
        />

        {(isProcessing || disabled) && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-md">
            <div className="text-sm text-muted-foreground">
              {isProcessing
                ? i18n.__('vaiu.topup.wallet.processing')
                : i18n.__('vaiu.topup.wallet.unavailable_amount')}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <CheckCircle className="w-3 h-3" />
        <span>{i18n.__('vaiu.topup.wallet.secure')}</span>
      </div>
    </div>
  );
};

export default React.memo(DigitalWalletButton);
